/**
 * @fileoverview Shared helpers for practice assignments (Spec 01) — progress
 * label building and completion math used by the instructor surfaces and the
 * student-portal checklist.
 */

import { recordedAt } from "@/lib/lessonHistory";

/** Map an assignment's status to a StatusPill kind. */
export const assignmentPillKind = (assignment) => {
  if (assignment.status === "open") return "Assigned";
  if (assignment.status === "completed") return "Completed";
  return "Closed";
};

/** Whether the assignment has any auto-tracked target (vs. manual mark-done). */
export const hasAutoTargets = (assignment) =>
  Boolean(assignment.target_sessions || assignment.target_minutes || assignment.rehearsal_requested);

/** "2/3 sessions · 40/60 min · rehearsal ✓" — only the targets that are set. */
export function assignmentProgressLabel(assignment) {
  const parts = [];
  if (assignment.target_sessions) {
    parts.push(`${assignment.progress_sessions ?? 0}/${assignment.target_sessions} sessions`);
  }
  if (assignment.target_minutes) {
    parts.push(`${assignment.progress_minutes ?? 0}/${assignment.target_minutes} min`);
  }
  if (assignment.rehearsal_requested) {
    parts.push(assignment.rehearsal_recording_id ? "rehearsal ✓" : "rehearsal pending");
  }
  return parts.join(" · ");
}

/**
 * Overall progress percent (0–100) across the set targets, or null when the
 * assignment has no auto targets (manual mark-done only).
 */
export function assignmentPercent(assignment) {
  if (assignment.status === "completed") return 100;
  const ratios = [];
  if (assignment.target_sessions) {
    ratios.push(Math.min(1, (assignment.progress_sessions || 0) / assignment.target_sessions));
  }
  if (assignment.target_minutes) {
    ratios.push(Math.min(1, (assignment.progress_minutes || 0) / assignment.target_minutes));
  }
  if (assignment.rehearsal_requested) {
    ratios.push(assignment.rehearsal_recording_id ? 1 : 0);
  }
  if (!ratios.length) return null;
  return Math.round((ratios.reduce((sum, r) => sum + r, 0) / ratios.length) * 100);
}

/**
 * The lessons "Assign practice" offers items from: the student's most recent
 * PERSONAL lessons that carry practice items, newest first (iOS review B5).
 *
 * Class lessons are left out on purpose: assigning a class lesson's item fans
 * out to every member of the class, which is not what an instructor means on
 * one student's page. Each item keeps its index in the lesson's own
 * `practice_items`, because that index is what the server resolves
 * (`practice_item_index`); blank items are skipped but never renumbered.
 *
 * @param {object[]} lessons The student's lessons (`GET /students/{id}/lessons`).
 * @param {number} [count=3] How many lessons to offer.
 * @returns {{lesson: object, items: {index: number, text: string}[]}[]}
 */
export function assignableLessons(lessons, count = 3) {
  return (lessons || [])
    .filter((lesson) => lesson && lesson.kind !== "class")
    .map((lesson) => ({
      lesson,
      items: (lesson.practice_items || [])
        .map((text, index) => ({ index, text: typeof text === "string" ? text.trim() : "" }))
        .filter((item) => item.text),
    }))
    .filter((group) => group.items.length > 0)
    .sort((a, b) => (recordedAt(b.lesson) ?? 0) - (recordedAt(a.lesson) ?? 0))
    .slice(0, count);
}

/**
 * Whether an item already has an OPEN assignment from the same lesson, so the
 * picker shows it as assigned rather than offering it twice.
 */
export function isItemAssigned(assignments, lessonId, text) {
  return (assignments || []).some(
    (a) => a.status === "open" && a.lesson_id === lessonId && (a.text || "").trim() === text,
  );
}
